import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../services/supabaseClient';
import {
  createAssignment, fetchMyAssignments, fetchSubmissions, fetchUsage,
  fetchAssignmentDetail, updateAssignment, deleteAssignment, getSubmissionAudioUrl,
} from '../services/assignmentApi';
import { trackEvent } from '../utils/analytics';

// 導入意向アンケート。
// ⚠️「有料でもいい」と「金額次第」は実質同義で、聞くとほぼ全員が後者を選ぶ。
//    判断が分かれる軸（継続意向・予算の出どころ・上限額）で聞く。
//    特に②は、価格そのものより営業のやり方を決める材料になる。
const SURVEY = [
  {
    key: 'intent',
    label: '① 体験後も続けて使いたいですか？',
    options: ['無料なら使いたい', '有料でも使いたい', 'たぶん使わない'],
  },
  {
    key: 'budget',
    label: '② 費用はどこから出せそうですか？',
    options: ['自費', '学校・学年の予算', '教育委員会など上位の予算', 'わからない'],
  },
  {
    key: 'price',
    label: '③ 1クラス分で月額いくらまでなら？',
    // ⚠️ Notionのselectオプションはカンマを含められないので「〜1,000円」にしない。
    //    文字列はNotion側と完全一致させること（ズレると集計が割れる）
    options: ['〜1000円', '〜3000円', '〜5000円', 'それ以上でも可'],
  },
];

// 役割の確認は「間違って入った人を引き返させる」ためのもので、認証ではない。
// 自己申告なので誰でも「先生」を選べる。目的は取り違え防止とGA4での実態把握。
const roleKey = (userId) => `ondoku_teacher_role_${userId}`;

// 課題一覧の初期表示件数。これを超える分は「もっとみる」で開く
const VISIBLE_ASSIGNMENTS = 3;

/**
 * 提出を生徒ごとにまとめる。
 * れんしゅうは回数と最高点、ほんばんは最新の1件を表示する。
 */
/** created_at（UTC）を端末のローカル日付 "YYYY-MM-DD" にする */
function localDay(iso) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 提出のある日付を新しい順で返す */
function submissionDays(submissions) {
  return [...new Set(submissions.map((s) => localDay(s.created_at)))].sort((a, b) => b.localeCompare(a));
}

/** その日にほんばんを出した人数（同じ子が複数回出しても1人と数える） */
function honbanCount(submissions, day) {
  return new Set(
    submissions
      .filter((s) => s.type === 'honban' && localDay(s.created_at) === day)
      .map((s) => s.student_name)
  ).size;
}

const DAY_WEEK = ['日', '月', '火', '水', '木', '金', '土'];
const fmtDay = (day) => {
  const [y, m, d] = day.split('-').map(Number);
  return `${m}/${d}（${DAY_WEEK[new Date(y, m - 1, d).getDay()]}）`;
};

/**
 * 指定した日の提出を生徒ごとにまとめる。
 * れんしゅうはその日の回数と最高点、ほんばんはその日の最新1件。
 *
 * ⚠️ 名簿は「その日の提出」ではなく**全期間**から作る。
 *    その日に何もしていない子こそ先生が知りたい相手なので、
 *    その日のデータだけで作ると画面から消えてしまう。
 */
function byStudent(submissions, day) {
  const map = new Map();
  for (const s of submissions) {
    if (!map.has(s.student_name)) {
      map.set(s.student_name, { name: s.student_name, renshyuCount: 0, renshyuBest: null, honban: null });
    }
    if (day && localDay(s.created_at) !== day) continue; // 名簿には載せるが中身は数えない

    const entry = map.get(s.student_name);
    if (s.type === 'honban') {
      // fetchSubmissions は新しい順なので、最初に見つかったものが最新
      if (!entry.honban) entry.honban = s;
    } else {
      entry.renshyuCount++;
      if (entry.renshyuBest === null || s.score > entry.renshyuBest) entry.renshyuBest = s.score;
    }
  }
  // 未提出を下に、あとは名前順
  return [...map.values()].sort((a, b) => {
    if (!!a.honban !== !!b.honban) return a.honban ? -1 : 1;
    return a.name.localeCompare(b.name, 'ja');
  });
}

/**
 * 詳細に出せる中身があるか。
 * ⚠️ transcript / ai_feedback / ai_details は2026-08-14に追加した列なので、
 *    それ以前の提出は全部 null。ボタンを出す前に必ず確認すること。
 */
const hasDetail = (sub) => Boolean(sub?.transcript || sub?.ai_feedback || sub?.ai_details);

/**
 * 提出記録をCSVにする。
 *
 * データを人質にして課金させない方針なので、無料・有料を問わず落とせる。
 * 体験終了後の自動削除の前に、先生が手元へ残す手段でもある。
 *
 * ⚠️ Excelで開く前提なので UTF-8 BOM を付ける。付けないと日本語が文字化けする。
 * ⚠️ セルの先頭が = + - @ だと Excel が数式として実行する（CSVインジェクション）。
 *    生徒名は自由入力なので、先頭に ' を足して無害化する。
 */
function submissionsToCsv(assignment, rows) {
  const esc = (v) => {
    let s = v == null ? '' : String(v);
    if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
    return `"${s.replace(/"/g, '""')}"`;
  };

  const header = ['課題', 'コード', '生徒名', 'モード', '点数', '提出日時', '文字起こし', '採点コメント', 'くわしいコメント'];
  const body = rows.map((r) => [
    assignment.title || '（タイトルなし）',
    assignment.id,
    r.student_name,
    r.type === 'honban' ? '本番' : '練習',
    r.score ?? '',
    new Date(r.created_at).toLocaleString('ja-JP'),
    r.transcript  ?? '',
    r.ai_feedback ?? '',
    r.ai_details  ?? '',
  ]);

  return '﻿' + [header, ...body].map((line) => line.map(esc).join(',')).join('\r\n');
}

function downloadCsv(filename, csv) {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// 課題と提出記録が消える日。
// ⚠️ api/keepalive.js の RECORD_KEEP_DAYS_AFTER_EXPIRY と同じ値。
//    片方だけ変えると「◯日に削除」の案内が嘘になるので、必ずセットで直すこと。
const RECORD_KEEP_DAYS_AFTER_EXPIRY = 30;
const purgeDate = (expiresAt) =>
  new Date(new Date(expiresAt).getTime() + RECORD_KEEP_DAYS_AFTER_EXPIRY * 86400000).toISOString();

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' });

const daysUntil = (iso) =>
  Math.max(0, Math.ceil((new Date(iso) - new Date()) / 86400000));

export default function TeacherScreen({ onBack, onContact, onLogin }) {
  // 設定から遷移してきた場合は画面遷移だけで戻る（onBackあり）。
  // URL直打ちの場合はプロフィール未ロードなので、通常のURL遷移でアプリを起動し直す。
  const goBack     = onBack ?? (() => { window.location.href = '/app'; });
  const backLabel  = onBack ? '← 設定に戻る' : '← アプリに戻る';
  const [teacherId, setTeacherId] = useState(null);
  const [role, setRole]           = useState(null); // 'teacher' | 'parent' | 'child' | null（未選択）
  const [loading, setLoading]     = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [openId, setOpenId]           = useState(null);   // 提出一覧を開いている課題
  const [submissions, setSubmissions] = useState([]);
  const [usage, setUsage]             = useState(null);   // 開いている課題の今日の使用状況
  const [detailSub, setDetailSub]     = useState(null);   // 詳細を開いている提出
  const [selectedDay, setSelectedDay] = useState(null);   // 提出一覧で見ている日（"YYYY-MM-DD"）
  const [confirmTrial, setConfirmTrial] = useState(false); // 体験開始の確認ポップアップ

  // 作成フォーム
  const [title, setTitle]     = useState('');
  const [body, setBody]       = useState('');
  const [grade, setGrade]     = useState(2);   // AI採点のフィードバック文体に使う
  const [saving, setSaving]   = useState(false);
  const [newCode, setNewCode] = useState(null);
  const [copied, setCopied]   = useState(null);
  const [showAll, setShowAll] = useState(false); // 課題一覧を全件表示するか

  const [linkModal, setLinkModal] = useState(null); // 配布リンクのポップアップ（課題1件）
  const [playing, setPlaying]     = useState(null); // 再生中の提出（生徒名）
  const audioRef = useRef(null);

  // 本文の表示・編集・削除ポップアップ
  const [detail, setDetail]         = useState(null);  // { id, title, body, ... }
  const [detailBusy, setDetailBusy] = useState(false);
  const [editing, setEditing]       = useState(false);
  const [editTitle, setEditTitle]   = useState('');
  const [editBody, setEditBody]     = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [detailSubs, setDetailSubs] = useState(0);      // 提出件数（削除・編集の警告用）

  // 導入意向アンケート
  const [survey, setSurvey]         = useState({});
  const [surveyNote, setSurveyNote] = useState('');
  const [surveySent, setSurveySent] = useState(false);
  const [surveyBusy, setSurveyBusy] = useState(false);

  const reload = useCallback(async (id) => {
    const [list, use] = await Promise.all([fetchMyAssignments(id), fetchUsage(id)]);
    setAssignments(list);
    setUsage(use);
  }, []);

  useEffect(() => {
    trackEvent('ondoku_teacher_screen_opened');
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        setTeacherId(session.user.id);
        setRole(localStorage.getItem(roleKey(session.user.id)));
        await reload(session.user.id);
      }
      setLoading(false);
    });
  }, [reload]);

  /**
   * 体験期間がまだ始まっていないか。
   * ⚠️ 判定はメーター表示と同じ `usage.trial.startedAt` を使う（別々に持つと案内が嘘になる）。
   *    usage が取れなかったときだけ、課題の有無で代用する。
   */
  const trialNotStarted = usage ? !usage.trial?.startedAt : assignments.length === 0;

  const handleCreate = () => {
    if (!body.trim()) return;
    // 1件目の作成で体験期間のカウントが始まってしまうので、その前に知らせる。
    // 2件目以降は既に始まっているため出さない（出すと嘘になる）。
    if (trialNotStarted) { setConfirmTrial(true); return; }
    doCreate();
  };

  const doCreate = async () => {
    setConfirmTrial(false);
    setSaving(true);
    try {
      const code = await createAssignment(teacherId, title.trim(), body.trim(), grade);
      trackEvent('ondoku_assignment_created', { grade: grade ?? 'unknown', is_first: trialNotStarted });
      setNewCode(code);
      setTitle('');
      setBody('');
      await reload(teacherId);
    } catch (e) {
      alert(`作成に失敗しました: ${e.message}`);
    }
    setSaving(false);
  };

  const handleOpen = async (id) => {
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id);
    const list = await fetchSubmissions(id);
    setSubmissions(list);
    // 毎日の宿題を想定しているので、開いたときは「いちばん新しい日」を出す
    setSelectedDay(submissionDays(list)[0] ?? null);
  };

  // 提出された録音を再生する。Storageは非公開なので都度署名付きURLを作る
  const playSubmission = async (name, audioPath) => {
    if (playing === name) {
      audioRef.current?.pause();
      setPlaying(null);
      return;
    }
    audioRef.current?.pause();
    const url = await getSubmissionAudioUrl(audioPath);
    if (!url) { alert('録音が見つかりませんでした（30日を過ぎると自動削除されます）'); return; }
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.onended = () => setPlaying(null);
    audio.play();
    setPlaying(name);
  };

  const openDetail = async (id) => {
    setDetailBusy(true);
    setEditing(false);
    setConfirmDelete(false);
    const [d, subs] = await Promise.all([fetchAssignmentDetail(id), fetchSubmissions(id)]);
    setDetail(d);
    setDetailSubs(subs.length);
    setEditTitle(d?.title ?? '');
    setEditBody(d?.body ?? '');
    setDetailBusy(false);
  };

  const handleUpdate = async () => {
    setDetailBusy(true);
    try {
      await updateAssignment(detail.id, { title: editTitle.trim(), body: editBody.trim() });
      await reload(teacherId);
      setDetail({ ...detail, title: editTitle.trim(), body: editBody.trim() });
      setEditing(false);
    } catch (e) {
      alert(`更新に失敗しました: ${e.message}`);
    }
    setDetailBusy(false);
  };

  const handleDelete = async () => {
    setDetailBusy(true);
    try {
      await deleteAssignment(detail.id);
      await reload(teacherId);
      if (openId === detail.id) setOpenId(null);
      setDetail(null);
    } catch (e) {
      alert(`削除に失敗しました: ${e.message}`);
    }
    setDetailBusy(false);
  };

  const chooseRole = (r) => {
    localStorage.setItem(roleKey(teacherId), r);
    setRole(r);
    trackEvent('ondoku_teacher_role_selected', { role: r });
  };

  const handleSurveySend = async () => {
    setSurveyBusy(true);
    try {
      const res = await fetch('/api/school-survey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          intent: survey.intent,
          budget: survey.budget,
          price:  survey.price,
          note:   surveyNote,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      trackEvent('ondoku_school_survey_sent', survey);
      setSurveySent(true);
    } catch (e) {
      console.error('[survey]', e);
      alert('送信に失敗しました。時間をおいて試すか、下の「個別に相談する」からお送りください。');
    }
    setSurveyBusy(false);
  };

  const linkFor = (code) => `${window.location.origin}/t/${code}`;

  const copyLink = (code) => {
    navigator.clipboard?.writeText(linkFor(code));
    setCopied(code);
    setTimeout(() => setCopied(null), 1800);
  };

  if (loading) {
    return (
      <div className="auth-loading">
        <div className="auth-loading-logo">Ondoku Quest</div>
        <div className="auth-loading-dot" />
      </div>
    );
  }

  // 未ログインでも「何ができるページなのか」は見せる。
  // 説明なしにログインだけ求めても、価値が伝わらず離脱するため。
  if (!teacherId) {
    return (
      <div className="screen teacher-screen">
        <button className="btn-back" onClick={goBack}>{backLabel}</button>
        <h2 className="teacher-title">🏫 先生ページ</h2>
        <p className="teacher-subtitle">
          クラスの児童に音読課題を配れる機能です
        </p>

        <div className="teacher-card teacher-feature-card">
          <ul className="teacher-feature-list">
            <li>
              課題を作ると<strong>配布リンク</strong>が発行され、
              ロイロノートやClassroomに貼るだけで配れます
            </li>
            <li>
              児童は<strong>ログイン不要</strong>。リンクを開いて名前を入れるだけ
            </li>
            <li>
              本番の音読は<strong>AIが採点</strong>し、結果が自動で先生に届きます
            </li>
            <li>
              児童ごとに<strong>練習の回数・点数</strong>と
              <strong>本番の提出状況</strong>を確認できます
            </li>
          </ul>
        </div>

        <div className="teacher-login-box">
          <p>
            ご利用には<strong>無料のアカウント</strong>が必要です。<br />
            いまなら最初の14日間、すべての機能を無料でお試しいただけます。
          </p>
          {/* アプリ内から来た場合は画面遷移で登録フォームへ直行。
              URL直打ちの場合はクエリでモードを伝える（トップの3択を経由させない） */}
          {onLogin ? (
            <button className="teacher-login-link" onClick={onLogin}>
              アカウントを作る・ログイン
            </button>
          ) : (
            <a className="teacher-login-link" href="/app?auth=register">
              アカウントを作る・ログイン
            </a>
          )}
        </div>
      </div>
    );
  }

  // ── 役割の確認（初回のみ）─────────────────────
  if (!role) {
    return (
      <div className="screen teacher-screen">
        <button className="btn-back" onClick={goBack}>{backLabel}</button>
        <h2 className="teacher-title">🏫 先生ページ</h2>
        <p className="teacher-subtitle">
          このページは、クラスに音読課題を配布する先生向けの機能です。<br />
          はじめに、どなたが使うか教えてください（どれを選んでも利用できます）
        </p>

        <button className="teacher-role-card" onClick={() => chooseRole('teacher')}>
          <span className="teacher-role-icon">🧑‍🏫</span>
          <span className="teacher-role-body">
            <span className="teacher-role-name">先生</span>
            <span className="teacher-role-desc">クラスの児童に音読課題を配布したい</span>
          </span>
        </button>

        <button className="teacher-role-card" onClick={() => chooseRole('parent')}>
          <span className="teacher-role-icon">👪</span>
          <span className="teacher-role-body">
            <span className="teacher-role-name">保護者</span>
            <span className="teacher-role-desc">自分の子どもの音読練習に使いたい</span>
          </span>
        </button>

        <button className="teacher-role-card" onClick={() => chooseRole('child')}>
          <span className="teacher-role-icon">🧒</span>
          <span className="teacher-role-body">
            <span className="teacher-role-name">こども</span>
            <span className="teacher-role-desc">じぶんで　おんどくの　れんしゅうを　したい</span>
          </span>
        </button>
      </div>
    );
  }

  // 役割で入室を制限はしない。自己申告なので弾いても「先生」を選び直されるだけで、
  // 意味のないハードルになる。選択はGA4での実態把握と、入る前の説明のために使う。

  return (
    <div className="screen teacher-screen">
      <button className="btn-back" onClick={goBack}>{backLabel}</button>

      <h2 className="teacher-title">🏫 先生ページ</h2>
      <p className="teacher-subtitle">
        音読課題を作成すると配布リンクが発行されます。<br />
        ロイロノートやClassroomに貼り付けて、クラスに配布できます。
      </p>

      <div className="teacher-scroll-area">

        {/* 体験期間切れの注意書き（課題作成カードの上・最初に目に入る位置） */}
        {usage?.trial?.expired && (
          <div className="teacher-meter-alert teacher-top-alert">
            体験期間が終了しています。課題の作成・編集・配布リンクの発行はこれまで通りできますが、
            <strong>生徒は配布リンクから音読の録音ができません</strong>。
            クーポンコードをお持ちの場合は設定画面から延長できます。
          </div>
        )}

        {/* ── 課題作成 ───────────────────────── */}
        <div className="teacher-card">
          <div className="teacher-card-title">✏️ 新しい課題を作成</div>
          <input
            className="teacher-input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="タイトル（例：スイミー 1〜3段落）"
          />
          <textarea
            className="teacher-textarea"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="本文を貼り付け（1行1文・改行で区切る）"
            rows={7}
          />

          <label className="teacher-field-label">
            学年（本番のAI採点コメントの言葉づかいが変わります）
          </label>
          <select
            className="teacher-input"
            value={grade}
            onChange={(e) => setGrade(Number(e.target.value))}
          >
            <option value={1}>小学1年</option>
            <option value={2}>小学2年</option>
            <option value={3}>小学3年</option>
            <option value={4}>小学4年</option>
            <option value={5}>小学5年</option>
            <option value={6}>小学6年</option>
          </select>
          <button
            className="teacher-btn-primary"
            onClick={handleCreate}
            disabled={saving || !body.trim()}
          >
            {saving ? '作成中…' : '課題を作成（期限14日）'}
          </button>

          {newCode && (
            <div className="teacher-link-box">
              <div className="teacher-link-label">✅ 配布リンクを発行しました</div>
              <div className="teacher-link-url">{linkFor(newCode)}</div>
              <button className="teacher-btn-sub" onClick={() => copyLink(newCode)}>
                {copied === newCode ? 'コピーしました' : 'リンクをコピー'}
              </button>
            </div>
          )}
        </div>

        {/* ── 課題一覧（1件ずつ別カード） ───────────── */}
        <div className="teacher-list-title">📋 作成した課題</div>

        {assignments.length === 0 && (
          <div className="teacher-card">
            <p className="teacher-empty">まだ課題がありません</p>
          </div>
        )}

        {(showAll ? assignments : assignments.slice(0, VISIBLE_ASSIGNMENTS)).map((a) => {
          const expired = a.expires_at && new Date(a.expires_at) < new Date();
          return (
            <div key={a.id}>
              {/* いつ出した課題か。カードの上に置いて一覧で追えるようにする */}
              {a.created_at && (
                <div className="teacher-created-label">
                  {new Date(a.created_at).toLocaleDateString('ja-JP', {
                    year: 'numeric', month: 'long', day: 'numeric', weekday: 'short',
                  })} 作成
                </div>
              )}
              <div className="teacher-card">
                <div className="teacher-assign-head">
                  <div style={{ minWidth: 0 }}>
                    <div className="teacher-assign-name">{a.title || '（タイトルなし）'}</div>
                    <div className={`teacher-assign-meta${expired ? ' expired' : ''}`}>
                      {expired
                        ? '期限切れ'
                        : `期限 ${new Date(a.expires_at).toLocaleDateString('ja-JP')}`}
                      {/* 黙って消さない。消える前にCSVで残せるよう日付を出しておく */}
                      {expired && a.expires_at && (
                        <span className="teacher-purge-mark">
                          🗑 {fmtDate(purgeDate(a.expires_at))}に削除
                        </span>
                      )}
                      {' ・ '}コード {a.id}
                      {a.updated_at && (
                        <span className="teacher-edited-mark">
                          ✏️ {new Date(a.updated_at).toLocaleDateString('ja-JP', {
                            month: 'numeric', day: 'numeric',
                          })} 編集
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="teacher-assign-actions">
                    <button className="teacher-btn-sub" onClick={() => setLinkModal(a)}>
                      リンク
                    </button>
                    <button className="teacher-btn-sub" onClick={() => openDetail(a.id)}>
                      本文
                    </button>
                    <button className="teacher-btn-sub" onClick={() => handleOpen(a.id)}>
                      {openId === a.id ? '閉じる' : '提出'}
                    </button>
                  </div>
                </div>

                {openId === a.id && (
                  <div className="teacher-sub-list">
                    {submissions.length === 0 && (
                      <p className="teacher-empty">まだ提出がありません</p>
                    )}

                    {/* 毎日提出されるので、日を選んでその日ぶんだけ見る。
                        全日ぶんを積み上げると人数×日数で画面が破綻する。
                        ⚠️ 期限14日ぶん＝最大14日になるので、タブを横に並べない。
                           1行に収まる「‹ セレクト ›」にして、日数が増えても選べるようにする */}
                    {submissions.length > 0 && (() => {
                      const days = submissionDays(submissions);
                      const i    = days.indexOf(selectedDay);
                      // days は新しい順。‹ は過去へ（index+1）、› は最近へ（index-1）
                      return (
                        <div className="teacher-day-picker">
                          <button
                            className="teacher-day-arrow"
                            onClick={() => setSelectedDay(days[i + 1])}
                            disabled={i >= days.length - 1}
                            aria-label="前の日"
                          >‹</button>

                          <select
                            className="teacher-day-select"
                            value={selectedDay ?? ''}
                            onChange={(e) => setSelectedDay(e.target.value)}
                          >
                            {days.map((day) => (
                              <option key={day} value={day}>
                                {fmtDay(day)}　提出{honbanCount(submissions, day)}人
                              </option>
                            ))}
                          </select>

                          <button
                            className="teacher-day-arrow"
                            onClick={() => setSelectedDay(days[i - 1])}
                            disabled={i <= 0}
                            aria-label="次の日"
                          >›</button>
                        </div>
                      );
                    })()}

                    {/* 手元に残す手段。体験終了後の自動削除の前にも使ってもらう。
                        その日ぶんではなく全期間を出す（バックアップとして使うため） */}
                    {submissions.length > 0 && (
                      <div className="teacher-csv-row">
                        <button
                          className="teacher-csv-btn"
                          onClick={() => downloadCsv(
                            `おんどく_${a.title || a.id}_${new Date().toISOString().slice(0, 10)}.csv`,
                            submissionsToCsv(a, submissions),
                          )}
                        >
                          ⬇ CSVで保存（全{submissions.length}件）
                        </button>
                      </div>
                    )}

                    {byStudent(submissions, selectedDay).map((s) => (
                      <div key={s.name} className="teacher-sub-row">
                        <span>
                          <span className="teacher-sub-name">{s.name}</span>
                          <span className="teacher-sub-practice">
                            練習{s.renshyuCount}回
                            {s.renshyuBest !== null && `（最高${s.renshyuBest}点）`}
                          </span>
                        </span>
                        <span className="teacher-sub-right">
                          {s.honban ? (
                            <>
                              {/* 詳細（文字起こし・採点コメント）。
                                  列を追加する前の提出には中身が無いので、あるときだけ出す */}
                              {hasDetail(s.honban) && (
                                <button
                                  className="record-detail-btn"
                                  onClick={() => setDetailSub(s)}
                                  title="文字起こしと採点コメントをみる"
                                >
                                  詳細
                                </button>
                              )}
                              {s.honban.audio_path && (
                                <button
                                  className="teacher-play-btn"
                                  onClick={() => playSubmission(s.name, s.honban.audio_path)}
                                  aria-label="録音を再生"
                                >
                                  {playing === s.name ? '⏸' : '▶'}
                                </button>
                              )}
                              <span className="teacher-sub-score">{s.honban.score}てん</span>
                              {/* 日付は上のタブで選んでいるので、ここは時刻だけでよい */}
                              <span className="teacher-sub-date">
                                {new Date(s.honban.created_at).toLocaleTimeString('ja-JP', {
                                  hour: '2-digit', minute: '2-digit',
                                })}
                              </span>
                            </>
                          ) : (
                            <span className="teacher-sub-pending">未提出</span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            );
        })}

        {assignments.length > VISIBLE_ASSIGNMENTS && (
          <button
            className="teacher-more-btn"
            onClick={() => setShowAll((v) => !v)}
          >
            {showAll
              ? '閉じる'
              : `もっとみる（残り${assignments.length - VISIBLE_ASSIGNMENTS}件）`}
          </button>
        )}

        {/* ── プランと利用状況（アカウント全体） ───────── */}
        {usage && <div className="teacher-list-title">💳 プランと利用状況</div>}
        {usage && (
          <div className="teacher-card teacher-usage-card">
            {usage.plan && (
              <div className="teacher-plan-row">
                <span className="teacher-plan-badge">{usage.plan.name}</span>
                <span className="teacher-plan-price">{usage.plan.priceLabel}</span>
                {usage.plan.seats && (
                  <span className="teacher-plan-seats">{usage.plan.seats}</span>
                )}
              </div>
            )}

            <div className="teacher-meter-head">
              {/* 体験中は暦月ではなく体験期間まるごとが枠なので、見出しを変える */}
              <span>{usage.resetAt ? '今月の利用状況' : '体験期間の利用状況'}</span>
              <strong className={usage.percent >= 80 ? 'warn' : ''}>
                {/* 使っているのに四捨五入で0%になると「動いていない」ように見える */}
                {usage.spent > 0 && usage.percent === 0 ? '1%未満' : `${usage.percent}%`}
              </strong>
            </div>
            <div className="teacher-meter-bar">
              <div
                className={`teacher-meter-fill${usage.percent >= 80 ? ' warn' : ''}`}
                // 少しでも使っていればバーを見せる（0%でも幅3px）
                style={{ width: usage.spent > 0 ? `max(3px, ${usage.percent}%)` : 0 }}
              />
            </div>
            <dl className="teacher-usage-rows">
              {usage.honban && (
                <div className="teacher-usage-row">
                  <dt>本番枠</dt>
                  <dd>
                    {usage.honban.quota}回分
                    <span className="teacher-usage-sub">
                      （{usage.honban.seatCount}人 × 1日1回 × {usage.honban.schoolDays}日）
                    </span>
                  </dd>
                </div>
              )}
              <div className="teacher-usage-row">
                <dt>使用回数</dt>
                <dd>本番 {usage.counts.honban}回 ／ 練習 {usage.counts.renshyu}回</dd>
              </div>
              <div className="teacher-usage-row">
                <dt>残り</dt>
                <dd>
                  本番 {usage.honban ? usage.honban.left : '-'}回分
                  ／ 練習 約{usage.renshyuLeft ?? 0}回分
                </dd>
              </div>
              {/* 有料プランに移った先生には体験期間を出さない（払っているのに
                  「体験期間 あと◯日」と出ると、切れると使えなくなるように見える） */}
              {usage.plan?.key === 'trial' && (
                usage.trial?.startedAt ? (
                  <div className="teacher-usage-row">
                    <dt>体験期間</dt>
                    <dd>
                      {fmtDate(usage.trial.startedAt)} 〜 {fmtDate(usage.trial.endsAt)}
                      {usage.trial.expired
                        ? <span className="teacher-trial-over">（終了）</span>
                        : `（あと${usage.trial.daysLeft}日）`}
                    </dd>
                  </div>
                ) : (
                  <div className="teacher-usage-row">
                    <dt>体験期間</dt>
                    {/* 起点は作成日ではなく「最初に児童が読んだ日」。
                        課題を早めに用意しても体験日数が減らないようにしてある */}
                    <dd>さいしょに児童が読んだ日から{usage.trial?.days ?? 14}日間</dd>
                  </div>
                )
              )}
              {usage.plan?.key === 'trial' && usage.trial?.extraDays > 0 && (
                <div className="teacher-usage-row">
                  <dt>クーポン利用</dt>
                  <dd>+{usage.trial.extraDays}日延長済み</dd>
                </div>
              )}
              {usage.resetAt && (
                <div className="teacher-usage-row">
                  <dt>次回リセット</dt>
                  <dd>{fmtDate(usage.resetAt)}（あと{daysUntil(usage.resetAt)}日）</dd>
                </div>
              )}
            </dl>

            <div className="teacher-meter-note">
              ※練習の残り回数は、本番枠をすべて使い切る前提で計算しています。
              本番の提出を優先して確保しているので、練習を多く使っても本番が出せなくなることはありません
            </div>
            {usage.plan?.note && (
              <div className="teacher-meter-note">※{usage.plan.note}</div>
            )}
            {usage.trial?.expired ? (
              <div className="teacher-meter-alert">
                体験期間が終了しました。生徒は配布リンクから録音できません。
                クーポンコードをお持ちの場合は設定画面から延長できます。
                継続をご希望の場合はご連絡ください
              </div>
            ) : usage.trial?.startedAt && usage.trial.daysLeft <= 3 ? (
              <div className="teacher-meter-alert">
                体験期間の終了まであと{usage.trial.daysLeft}日です
              </div>
            ) : usage.percent >= 80 ? (
              <div className="teacher-meter-alert">
                残りわずかです。上限に達すると、今月は録音できなくなります
              </div>
            ) : null}
          </div>
        )}

        {/* ── 体験期間と今後のプラン ───────────────── */}
        {usage && (
          <div className="teacher-card">
            <div className="teacher-card-title">💡 体験期間について</div>
            <p className="teacher-body-text">
              最初に課題を作成した日から<strong>{usage.trial?.days ?? 14}日間</strong>、
              すべての機能を無料でお使いいただけます。
              期間が終わると、生徒は配布リンクから録音できなくなります
              （作成済みの課題や提出記録は残ります）。
              クーポンコードをお持ちの場合は、設定画面の「クーポンコード」欄から延長できます。
            </p>

            {usage.planPreview?.length > 0 && (
              <>
                <div className="teacher-plan-preview-head">
                  体験期間後のプラン<span className="teacher-soon-badge">準備中</span>
                </div>
                <ul className="teacher-plan-preview">
                  {usage.planPreview.map((p) => (
                    <li key={p.key}>
                      <span className="teacher-plan-preview-name">{p.name}</span>
                      <span className="teacher-plan-preview-seats">{p.seats}</span>
                    </li>
                  ))}
                </ul>
                <p className="teacher-note-text">
                  料金は準備中です。決まりしだいこの画面でお知らせします。
                </p>
              </>
            )}

            <p className="teacher-note-text">
              ご意向は下のアンケートでお聞かせください。
            </p>
          </div>
        )}

        {/* ── できること（初見の人向けの説明） ────────── */}
        <div className="teacher-card teacher-feature-card">
          <div className="teacher-card-title">📚 このページでできること</div>
          <ul className="teacher-feature-list">
            <li>
              課題を作成すると<strong>配布リンク</strong>が発行されます。
              ロイロノートやClassroomに貼り付けるだけで配れます
            </li>
            <li>
              生徒は<strong>ログイン不要</strong>。リンクを開いて名前を入れるだけで始められます
            </li>
            <li>
              <strong>練習は1日3回</strong>まで。その場で点数が出るので、
              納得いくまで読んでから提出できます
            </li>
            <li>
              <strong>本番は1日1回</strong>。AIが採点し、
              結果は<strong>自動で先生に送られます</strong>
            </li>
            <li>
              先生は生徒ごとに、<strong>練習の回数・点数</strong>と
              <strong>本番の提出状況・点数</strong>を確認できます
            </li>
            <li>
              課題には期限があります（初期設定は14日）。
              期限が切れると本文は表示されなくなります
            </li>
          </ul>
        </div>

        {/* ── お知らせとアンケート ─────────────────── */}
        <div className="teacher-list-title">📢 お知らせとアンケート</div>

        <div className="teacher-card teacher-notice-card">
          <div className="teacher-card-title">⚠️ 今後の変更について</div>
          <p className="teacher-body-text">
            現在は無料でお使いいただいていますが、今後
            <strong>AI採点のご利用にはログインが必要</strong>になる予定です。
            配布リンクから生徒が読む流れは、これまでどおり
            <strong>ログイン不要のまま</strong>です（学校の端末で使えるようにするため）。
          </p>
          <p className="teacher-body-text">
            すでにご利用いただいている先生には、
            上記のプランへの移行をお願いすることになります。
            移行の時期や方法は、決まりしだいこの画面でお知らせします。
          </p>
        </div>

        {/* ── 導入意向アンケート ───────────────────── */}
        <div className="teacher-card">
          <div className="teacher-card-title">📝 アンケート（3問）</div>

          {surveySent ? (
            <p className="teacher-body-text">
              ご回答ありがとうございました。今後の参考にさせていただきます。
            </p>
          ) : (
            <>
              <p className="teacher-body-text">
                料金プランを決めるための参考にさせてください。匿名でも構いません。
              </p>

              {SURVEY.map((q) => (
                <div key={q.key} className="teacher-survey-q">
                  <div className="teacher-survey-label">{q.label}</div>
                  <div className="teacher-survey-options">
                    {q.options.map((opt) => (
                      <button
                        key={opt}
                        className={`teacher-survey-opt${survey[q.key] === opt ? ' active' : ''}`}
                        onClick={() => setSurvey((s) => ({ ...s, [q.key]: opt }))}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              <textarea
                className="teacher-textarea"
                value={surveyNote}
                onChange={(e) => setSurveyNote(e.target.value)}
                placeholder="ご意見・ご要望（任意）"
                rows={3}
              />

              <button
                className="teacher-btn-primary"
                onClick={handleSurveySend}
                disabled={surveyBusy || Object.keys(survey).length === 0}
              >
                {surveyBusy ? '送信中…' : 'アンケートを送る'}
              </button>
            </>
          )}

          <button className="teacher-btn-text" onClick={onContact ?? goBack}>
            個別に相談する（メッセージを送る）
          </button>
        </div>

      </div>

      {/* ── 配布リンク ─────────────────────────── */}
      {linkModal && (
        <div className="teacher-modal-overlay" onClick={() => setLinkModal(null)}>
          <div className="teacher-modal" onClick={(e) => e.stopPropagation()}>
            <div className="teacher-modal-head">
              <span className="teacher-modal-title">{linkModal.title || '（タイトルなし）'}</span>
              <button className="teacher-modal-close" onClick={() => setLinkModal(null)}>×</button>
            </div>

            <div className="teacher-share-label">リンクを配る</div>
            <div className="teacher-share-url">{linkFor(linkModal.id)}</div>
            <button className="teacher-btn-primary" onClick={() => copyLink(linkModal.id)}>
              {copied === linkModal.id ? '✅ コピーしました' : 'リンクをコピー'}
            </button>
            <p className="teacher-note-text">
              ロイロノートやClassroomに貼り付けてください。
            </p>

            <div className="teacher-share-label" style={{ marginTop: '18px' }}>
              コードを伝える
            </div>
            {/* 板書や口頭で伝える想定。読み間違いを防ぐため大きく表示する */}
            <div className="teacher-share-code">{linkModal.id}</div>
            <p className="teacher-note-text">
              リンクを貼れないときは、このコードを児童に伝えてください。
              生徒ページ（{window.location.origin}/s）でコードを入力すると開けます。<br />
              まぎらわしい文字（l・o・0・1）は使っていません。
            </p>
          </div>
        </div>
      )}

      {/* ── 体験期間の開始確認（1件目の作成時のみ） ───── */}
      {confirmTrial && (
        <div className="teacher-modal-overlay" onClick={() => setConfirmTrial(false)}>
          <div className="teacher-modal" onClick={(e) => e.stopPropagation()}>
            <div className="teacher-modal-head">
              <span className="teacher-modal-title">無料体験について</span>
              <button className="teacher-modal-close" onClick={() => setConfirmTrial(false)}>×</button>
            </div>

            <div className="teacher-trial-notice">
              <p>
                無料体験は<strong>{usage?.trial?.days ?? 14}日間</strong>です。
                <strong>最初のお子さんが読んだ日</strong>から数えはじめます。
              </p>
              <ul>
                <li>課題を作っただけでは、まだ始まりません</li>
                <li>体験期間中の費用はかかりません</li>
                <li>期間が終わると、児童は録音・採点ができなくなります</li>
                <li>作成した課題と提出記録は、期間が終わっても残ります</li>
              </ul>
              <p className="teacher-trial-notice-sub">
                ※ 前もって課題を用意しておいても、体験できる日数は減りません。
              </p>
            </div>

            <div className="teacher-modal-actions">
              <button className="teacher-btn-sub" onClick={() => setConfirmTrial(false)}>
                やめる
              </button>
              <button className="teacher-btn-primary" onClick={doCreate} disabled={saving}>
                {saving ? '作成中…' : '課題を作成する'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 提出の詳細（文字起こし・採点コメント） ───── */}
      {detailSub && (
        <div className="teacher-modal-overlay" onClick={() => setDetailSub(null)}>
          <div className="teacher-modal" onClick={(e) => e.stopPropagation()}>
            <div className="teacher-modal-head">
              <span className="teacher-modal-title">{detailSub.name}さん</span>
              <button className="teacher-modal-close" onClick={() => setDetailSub(null)}>×</button>
            </div>

            <div className="teacher-sub-detail-meta">
              <span className="teacher-sub-score">{detailSub.honban.score}てん</span>
              <span className="teacher-sub-date">
                {new Date(detailSub.honban.created_at).toLocaleString('ja-JP', {
                  month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit',
                })}
                {' ・ '}練習{detailSub.renshyuCount}回
                {detailSub.renshyuBest !== null && `（最高${detailSub.renshyuBest}点）`}
              </span>
            </div>

            {detailSub.honban.ai_feedback && (
              <div className="record-detail-section">
                <div className="record-detail-section-label">💬 採点コメント</div>
                <div className="record-detail-feedback">{detailSub.honban.ai_feedback}</div>
              </div>
            )}

            {detailSub.honban.ai_details && (
              <div className="record-detail-section">
                <div className="record-detail-section-label">📝 くわしいコメント</div>
                <div className="record-detail-comment">{detailSub.honban.ai_details}</div>
              </div>
            )}

            {detailSub.honban.transcript && (
              <div className="record-detail-section">
                {/* 音声認識の結果なので、読み方の評価そのものではない旨を添える */}
                <div className="record-detail-section-label">🎤 読み取った文（音声認識）</div>
                <div className="record-detail-transcript">{detailSub.honban.transcript}</div>
              </div>
            )}

            <button className="teacher-btn-sub teacher-sub-detail-close" onClick={() => setDetailSub(null)}>
              閉じる
            </button>
          </div>
        </div>
      )}

      {/* ── 本文の表示・編集・削除 ───────────────── */}
      {detail && (
        <div className="teacher-modal-overlay" onClick={() => setDetail(null)}>
          <div className="teacher-modal" onClick={(e) => e.stopPropagation()}>
            <div className="teacher-modal-head">
              <span className="teacher-modal-title">
                {editing ? '課題を編集' : (detail.title || '（タイトルなし）')}
              </span>
              <button className="teacher-modal-close" onClick={() => setDetail(null)}>×</button>
            </div>

            {editing ? (
              <>
                {detailSubs > 0 && (
                  <div className="teacher-meter-alert">
                    すでに{detailSubs}件の提出があります。本文を変えると、
                    提出済みの点数は「変更前の本文」で採点されたものになり、
                    あとから提出する児童と単純に比較できなくなります
                  </div>
                )}
                <input
                  className="teacher-input"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder="タイトル"
                />
                <textarea
                  className="teacher-textarea"
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  rows={10}
                />
                <button
                  className="teacher-btn-primary"
                  onClick={handleUpdate}
                  disabled={detailBusy || !editBody.trim()}
                >
                  {detailBusy ? '保存中…' : '保存する'}
                </button>
                <button className="teacher-btn-text" onClick={() => setEditing(false)}>
                  キャンセル
                </button>
              </>
            ) : (
              <>
                <div className="teacher-modal-meta">
                  コード {detail.id} ・ 期限 {new Date(detail.expires_at).toLocaleDateString('ja-JP')}
                  {detailSubs > 0 && ` ・ 提出 ${detailSubs}件`}
                  {detail.updated_at && (
                    <span className="teacher-edited-mark">
                      ✏️ {new Date(detail.updated_at).toLocaleString('ja-JP', {
                        month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit',
                      })} に編集
                    </span>
                  )}
                </div>
                <pre className="teacher-modal-body">{detail.body}</pre>

                {/* 確認中は隠す。同じ「削除する」が2つ並ぶとどちらが実行か分からない */}
                {!confirmDelete && (
                  <div className="teacher-modal-actions">
                    <button className="teacher-btn-sub" onClick={() => setEditing(true)}>
                      編集する
                    </button>
                    <button
                      className="teacher-btn-sub teacher-btn-danger"
                      onClick={() => setConfirmDelete(true)}
                    >
                      削除する
                    </button>
                  </div>
                )}

                {confirmDelete && (
                  <div className="teacher-modal-danger">
                    <p>
                      この課題を削除します。
                      {detailSubs > 0
                        ? <><strong>児童の提出記録{detailSubs}件も一緒に消えます。</strong>元に戻せません。</>
                        : '元に戻せません。'}
                      <br />配布済みのリンクも開けなくなります。
                    </p>
                    <div className="teacher-modal-actions">
                      <button className="teacher-btn-sub" onClick={() => setConfirmDelete(false)}>
                        やめる
                      </button>
                      <button
                        className="teacher-btn-sub teacher-btn-danger"
                        onClick={handleDelete}
                        disabled={detailBusy}
                      >
                        {detailBusy ? '削除中…' : 'はい、削除する'}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
